<template>
  <div class="vendor-menu-options menu-item">
    <menu-item-image class="menu-item__image" :images="item.images" :is-background="true" :full-res-only="false" />
    <div class="menu-item__body d-flex">
      <div v-if="selectedLevel == 0">
        <label for="menu-item-close-btn" class="iw-visuallyhidden">close menu item</label>
        <button id="menu-item-close-btn" class="close menu-item-close" @click="hideModal"></button>
        <menu-item-info :menu-item="item" :item-price="item.cost"></menu-item-info>
      </div>
      <div v-else class="menu-item__back-button" @click="goBack">
        <img src="../../../../assets/images/icon-back-arrow.svg" />
      </div>
    </div>
    <div class="menu-item__modifier-name">{{ selectedParentModifierName }}</div>
    <div class="menu-item__modifiers">
      <template v-for="modifierGroup in item.modifierGroups" :key="modifierGroup.name">
        <ModifierGroup
          ref="modifier-group"
          :modifier-group="modifierGroup"
          :level="0"
          :selected-level="selectedLevel"
          :parent-modifier="null"
          :modifiers-path="modifiersPath"
          :submitted="submitted"
          @onUpdateSelection="onUpdateSelection"
        ></ModifierGroup>
      </template>
      <div v-if="hasToShowSpecialInstructions()" class="menu-item__modifiers__instructions">
        <special-instructions v-model="specialInstructions"></special-instructions>
      </div>
    </div>
    <div class="vendor-menu-modal__footer menu-item__footer">
      <template v-if="selectedLevel == 0">
        <app-counter v-model="count" :max="getMaxQty" :min="getMinQty" :is-disabled="isButtonDisabled" />
        <app-button
          class="menu-item__add-to-cart-btn"
          :text="actionTitle"
          :aria-label="actionTitle"
          :handler="processItem"
          :is-disabled="isButtonDisabled"
        />
      </template>
      <template v-else>
        <app-button class="menu-item__save-btn" text="Save" :aria-label="'Save'" :handler="goBack" />
      </template>
    </div>
    <p v-if="isButtonDisabled && !submitted" class="vendor-menu-modal__error-message">
      {{ addToCartDisabledMessage }}
    </p>
  </div>
</template>

<!-- prettier-ignore -->

<script>
import MenuItemInfo from "./MenuItemInfo.vue";
import AppCounter from "../../counter/AppCounter.vue";
import ModifierGroup from "./modifier-group/ModifierGroup.vue";
import SpecialInstructions from "./SpecialInstructions.vue";
import MenuItemImage from "../../../../routes/menu/components/menu-list/menu-item/menu-item-image/MenuItemImage.vue";
import AppButton from "../../../../components/base/app-button/AppButton.vue";
import { ref } from "vue";
import { mapActions, mapGetters, useStore } from "vuex";
import { cloneDeep } from "lodash";
import { showDialog, MODAL } from "@/utils";
import { ITEM_LIMIT_REACHED_MSG } from "@/../app-constants";

export default {
  name: "MenuItem",
  components: { MenuItemImage, MenuItemInfo, AppCounter, AppButton, ModifierGroup, SpecialInstructions },
  props: {
    menuItem: Object,
    quantity: Number,
    instructions: String,
    index: Number,
    isEdit: Boolean
  },
  async setup(props) {
    if (props.menuItem?.hasModifiers && !props.isEdit) {
      const store = useStore();

      const fullItem = await store.dispatch("vendor/fetchVendorItemById", props.menuItem.id);

      const initModifiers = (item, parentSelected = true) => {
        item?.modifierGroups?.forEach((modifierGroup) => {
          modifierGroup.selectedModifiers = modifierGroup.multipleChoice ? [] : "";
          modifierGroup?.modifiers?.forEach((modifier) => {
            if (modifier?.rules.default && parentSelected) {
              if (modifierGroup.multipleChoice) {
                modifierGroup.selectedModifiers.push(modifier.modifierId);
              } else {
                modifierGroup.selectedModifiers = modifier.modifierId;
              }
            }
            initModifiers(modifier, modifier?.rules.default);
          });
          if (modifierGroup.multipleChoice) modifierGroup.selectedModifiers.sort();
        });
      };

      initModifiers(fullItem);

      return { item: ref(fullItem) };
    }
    return { item: ref(cloneDeep(props.menuItem)) };
  },
  data() {
    return {
      count: this.itemQuantity || this.quantity || 1,
      specialInstructions: this.instructions || "",
      selectedLevel: 0,
      modifiersPath: [],
      selectedModifierName: "",
      submitted: false,
      isValidSelection: false,
      addToCartDisabledMessage: ""
    };
  },
  computed: {
    ...mapGetters({
      alcoholLimitMessagePerOrder: "vendor/getOrderAlcoholLimitMessage",
      getVenueSpecialInstructionIsActive: "venue/getVenueSpecialInstructionIsActive",
      isAlcoholLimitReached: "cart/isAlcoholLimitReached",
      alcoholRestrictionMessage: "vendor/getAlcoholRestrictionMessage",
      maxAlcoholPerTransaction: "vendor/maxAlcoholPerOrder",
      getCountInCart: "cart/getItemsCountInCart"
    }),
    getMaxQty() {
      if (this.item.isAlcohol && this.maxAlcoholPerTransaction) {
        return this.maxAlcoholPerTransaction;
      }

      // If maxQtyPerTransaction isn't set (undefined), then return that to 
      // the component which will not set an upper limit
      if (!this.item.maxQtyPerTransaction) {
        return this.item.maxQtyPerTransaction;
      }

      const maxQtyPerTransaction = +this.item.maxQtyPerTransaction;
      const totalInCart = this.getCountInCart(this.item) ?? 0;
      
      const quantity = this.quantity ?? 0;
      const modifiedCount = this.isEdit ? this.count - quantity : 0;
      // AvailableMax takes the setting of maxQty and subtracts what is currently in 
      // the cart, as well as any edits the user makes while amending the count.
      const availableMax = maxQtyPerTransaction - (totalInCart - modifiedCount);
      return quantity + availableMax;

    },
    getMinQty() {
      const totalInCart = this.getCountInCart(this.item) ?? 0;
      const minQtyPerTransaction = +this.item.minQtyPerTransaction;
      // Quantity is undefined when adding from the cart.  Quantity is set
      // as the original number when editing an item's count.
      const quantity = this.quantity ?? 0;

      // When determining if there are already the required min
      // items in the cart, we take the totalInCart and subtract the 
      // quantity when editing to ensure the user doesn't go below the 
      // minimum quanity for the order. 
      if ((totalInCart - quantity) >= minQtyPerTransaction) {
        return 1;
      }
   
      return minQtyPerTransaction;
    },
    actionTitle() {
      return this.isEdit ? "Edit item" : "Add to cart";
    },
    isButtonDisabled() {
      const itemCount = this.isEdit ? this.count - this.quantity : this.count;
      const alcoholLimitReached = this.item.isAlcohol && this.isAlcoholLimitReached(itemCount);

      const totalInCart = this.getCountInCart(this.item) ?? 0;

      const maxQtyReached = itemCount + totalInCart > +this.item.maxQtyPerTransaction;

      if (alcoholLimitReached) {
        this.addToCartDisabledMessage = this.alcoholLimitMessagePerOrder;
      } else if (maxQtyReached) {
        this.addToCartDisabledMessage = ITEM_LIMIT_REACHED_MSG;
      } else {
        this.addToCartDisabledMessage = "";
      }

      return alcoholLimitReached || maxQtyReached;
    },
    selectedParentModifierName() {
      return this.modifiersPath[this.modifiersPath.length - 1]?.name;
    }
  },
  methods: {
    ...mapActions({
      addItemToCart: "cart/addItemToCart",
      editItemInCart: "cart/editItemInCart"
    }),
    hideModal() {
      this.$modal.hide("item");
    },
    processItem() {
      this.submitted = true;
      this.checkValidSelection();

      if (this.isValidSelection) {
        this.hideModal();
        if (!this.isEdit) {
          if (this.item.isAlcohol) {
            showDialog(this, "", this.alcoholRestrictionMessage, [], true, false, () => {
              this.$modal.hide(MODAL.dialogName);
              this.addItemToCart({ item: this.item, quantity: this.count, specialInstructions: this.specialInstructions });
            });
          } else {
            this.addItemToCart({ item: this.item, quantity: this.count, specialInstructions: this.specialInstructions });
          }

          return;
        }
        this.editItemInCart({
          index: this.index,
          item: this.item,
          quantity: this.count,
          specialInstructions: this.specialInstructions
        });
        return;
      }
      this.scrollToInvalidModifierGroup();
    },
    onUpdateSelection(payload) {
      if (payload.goToNextLevel) {
        this.selectedLevel++;
        this.modifiersPath.push(payload.selectedParentModifier);
      }

      this.setSelectedModifiers(this.item, payload);
      this.checkValidSelection();
    },
    setSelectedModifiers(item, payload) {
      item?.modifierGroups?.forEach((modifierGroup) => {
        if (modifierGroup.id === payload.id) {
          modifierGroup.selectedModifiers = modifierGroup.multipleChoice
            ? payload.selectedModifiers.sort()
            : payload.selectedModifiers;
        } else {
          modifierGroup.modifiers?.forEach((modifier) => {
            this.setSelectedModifiers(modifier, payload);
          });
        }
      });
    },
    hasToShowSpecialInstructions() {
      return this.getVenueSpecialInstructionIsActive && this.item.hasModifiers;
    },
    goBack() {
      this.selectedLevel--;
      this.modifiersPath.splice(this.modifiersPath.length - 1);
    },
    scrollToInvalidModifierGroup() {
      const invalidGroups = this.$refs["modifier-group"]?.filter((ref) => !ref.isValid);
      if (invalidGroups?.length) invalidGroups[0].$el?.scrollIntoView({ behavior: "smooth" });
    },
    checkValidSelection() {
      this.isValidSelection =
        !this.$refs["modifier-group"] ||
        this.$refs["modifier-group"].every((ref) => {
          return ref.isValid;
        });
    }
  }
};
</script>

<style lang="scss">
.menu-item {
  font-family: $group-3-font-family-1;
  color: $group-3-color-2;

  @include respond-to-mobile {
    padding-bottom: 60px;
  }

  &__body {
    padding: 20px 24px 0 24px;
    @include respond-to-mobile {
      padding: 20px 16px 0 16px;
    }
  }

  &__modifiers {
    padding: 0px 24px 0 24px;
    @include respond-to-mobile {
      padding: 0px 16px 0 16px;
    }

    &__instructions {
      border-top: 12px solid rgba(141, 154, 174, 0.08);
    }
  }

  &__image {
    flex-basis: 334px !important;
    padding-right: 0 !important;
    max-height: 334px;
    height: 334px;
    overflow: hidden;
    background-size: cover !important;
    background-position-y: 50%;

    @include respond-to-mobile {
      flex-basis: 214px !important;
      max-height: 214px;
    }
  }
  &__modifier-name {
    font-size: 24px;
    font-weight: bold;
    padding-bottom: 3px;
    padding-left: 24px;
  }

  &__add-to-cart-label {
    text-align: center;
    color: $group-1-color-2;
  }

  &__add-to-cart-btn {
    &.btn {
      height: 46px;
      margin-left: 16px;
    }
  }

  &__back-button {
    cursor: pointer;
    width: 40px;
    height: 36px;
    background-color: transparent;
    margin-top: 11px;
  }

  &__save-btn {
    &.btn {
      height: 46px;
    }
  }

  &__back {
    margin-top: 11px;
  }
  &__footer {
    box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.5);
    @include respond-to-mobile {
      max-width: 500px;
      position: fixed;
      bottom: 0;
      z-index: 2;
    }
    box-sizing: border-box;
    padding: 16px 24px;
    @include respond-to-mobile {
      padding: 16px;
    }
    background-color: $group-1-color-2;
  }
  &__item-name {
    font-size: 24px;
    font-weight: bold;
    margin-bottom: 2px;
  }
}

.menu-item-close {
  border: 0;
  right: 14px;
  border-radius: 16px;
  background-color: white;
  margin: 10px 10px 0 0;
  @include respond-to-mobile {
    margin: 4px 2px 0 0;
  }

  &:before,
  &:after {
    height: 16px;
    width: 2px;
    background-color: $group-3-color-2;
  }
}
</style>
